import { Injectable } from '@angular/core';
import { AnalyticsService } from './analytics.service';
import { CLIENT_EVENTS, MARKETPLACE_EVENTS } from './analytics.events';
import { EventProperties } from './analytics.types';
import { trackOnce } from './once';


@Injectable({ providedIn: 'root' })
export class VendorDiscoveryTracker {
  constructor(private analytics: AnalyticsService) {}


  searched(params: {
    service?: string;
    subService?: string;
    city?: string;
    resultsShown: number;
    totalFound?: number;
    source: string;
  }): void {
    try {
      this.analytics.track(CLIENT_EVENTS.VENDOR_SEARCH, {
        service: params.service ?? null,
        sub_service: params.subService ?? null,
        city: params.city ?? null,
        results_shown: params.resultsShown,
        total_found: params.totalFound ?? null,
        // How much of the qualifying pool the client actually saw.
        results_withheld:
          typeof params.totalFound === 'number'
            ? Math.max(0, params.totalFound - params.resultsShown)
            : null,
        has_results: params.resultsShown > 0,
        source: params.source,
      });
    } catch { /* never break search */ }
  }

  /** A vendor card was opened / expanded. */
  vendorViewed(vendor: any, source: string, rank?: number): void {
    try {
      // Once per vendor per tab. Expanding a card, collapsing it and expanding
      // again is ONE person looking at ONE vendor — counting it as three would
      // make "vendors viewed per search" a measure of fidgeting.
      const code = vendor?.contributor_code || vendor?.id;
      if (!trackOnce(`vendor_viewed:${source}:${code}`)) return;
      this.analytics.track(CLIENT_EVENTS.VENDOR_VIEWED, {
        ...this.vendorProperties(vendor),
        rank: rank ?? null,
        source,
      });
    } catch { /* never break search */ }
  }

  /** The client opened a vendor's portfolio — a strong intent signal. */
  portfolioViewed(vendor: any, source: string): void {
    try {
      // Once per vendor per tab, same reasoning. The portfolio opens in a new
      // tab, so a client returning and clicking again is re-opening what they
      // already have — not a second act of evaluation.
      const code = vendor?.contributor_code || vendor?.id;
      if (!trackOnce(`portfolio_viewed:${source}:${code}`)) return;
      this.analytics.track(CLIENT_EVENTS.VENDOR_PORTFOLIO_VIEWED, {
        ...this.vendorProperties(vendor),
        source,
      });
    } catch { /* never break search */ }
  }

  vendorsCompared(vendors: any[], source: string): void {
    try {
      const list = vendors || [];
      this.analytics.track(CLIENT_EVENTS.VENDOR_COMPARED, {
        vendor_count: list.length,
        vendor_codes: list.map((v) => String(v?.contributor_code || '')).filter(Boolean),
        source,
      });
    } catch { /* never break search */ }
  }

  vendorInvited(
    vendor: any,
    params: { source: string; mode: 'single' | 'bulk'; batchSize?: number; rank?: number; projectId?: string },
  ): void {
    try {
      const props: EventProperties = {
        ...this.vendorProperties(vendor),
        invite_mode: params.mode,
        batch_size: params.batchSize ?? 1,
        rank: params.rank ?? null,
        project_id: params.projectId ?? null,
        source: params.source,
      };
      this.analytics.track(CLIENT_EVENTS.VENDOR_INVITED, props);
      this.analytics.track(MARKETPLACE_EVENTS.MARKETPLACE_VENDOR_INVITED, props);
    } catch { /* never break invite */ }
  }

  inviteBatchCompleted(params: {
    sent: number;
    failed: number;
    skipped: number;
    source: string;
    projectId?: string;
  }): void {
    try {
      const total = params.sent + params.failed + params.skipped;
      this.analytics.track(CLIENT_EVENTS.VENDOR_INVITED, {
        batch_summary: true,
        invited_count: params.sent,
        failed_count: params.failed,
        skipped_count: params.skipped,
        attempted_count: total,
        success_rate: total > 0 ? Math.round((params.sent / total) * 100) : null,
        project_id: params.projectId ?? null,
        source: params.source,
      });
    } catch { /* never break invite */ }
  }


  private vendorProperties(vendor: any): EventProperties {
    const v = vendor || {};
    const services: string[] = v.services || [];
    const home: string[] = v.home_locations || [];
    const serviceable: string[] = v.serviceable_locations || [];
    return {
      vendor_id: v.id != null ? String(v.id) : null,
      vendor_code: v.contributor_code || null,
      // The doc's "Category".
      category: services.length ? services[0] : null,
      services: services.length ? services : null,
      city: home.length ? home[0] : serviceable.length ? serviceable[0] : null,
      vendor_type: v.vendor_type || null,
      experience_band: v.experience_band || null,
      portfolio_quality: v.portfolio_quality ?? null,
      overall_score: v.overall_score ?? null,
      samples_count: v.samples_count ?? null,
      readiness_status: v.readiness_status || null,
      // Why search ranked them here — lets "clients ignore our top result" be
      // measured rather than guessed at.
      blended_score: v.blended_score ?? null,
      match_count: v.match_count ?? null,
    };
  }
}
